import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import type { FormEvent } from 'react';

import { documentSettingsQuery, openFile } from '../api/documents.ts';
import {
  REASON,
  addCertificate,
  addInsuranceKind,
  certificatesQuery,
  insuranceKindsQuery,
  jobInsuranceGapsQuery,
  requiredCoversQuery,
  retireCertificate,
  setRequiredCovers,
  upcomingInsuranceGapsQuery,
  uploadCertificatePdf,
  type Certificate,
  type InsuranceGap,
  type RequiredCover,
} from '../api/insurance.ts';
import { meQuery } from '../api/me.ts';
import { dayWithYear, tenantDay } from '../api/quotes.ts';
import { Button } from '../components/button.tsx';
import { AddButton, RemoveButton } from '../components/action-buttons.tsx';
import { Field } from '../components/field.tsx';
import { PageHeader } from '../components/page-header.tsx';
import { dollarsToMinorUnits, formatMinorUnits, minorUnitsToDollars } from '../money.ts';

// CB-REQ CMP-06 and VEN-05 at v1.966, compliance part 2: the company's certificates of currency,
// what each venue asks to see, and the jobs whose venue's cover is not met. Flagged, never refused:
// the database works a gap out (venue_cover_gaps) and nothing here decides one.

/** "$20,000,000 public liability: lapses before the job ends". */
export function gapLabel(g: InsuranceGap): string {
  const minimum = g.minimum
    ? ` (${formatMinorUnits(g.minimum.amountMinor, g.minimum.currency).replace(/\.00$/, '')})`
    : '';
  return `${g.kind.name}${minimum}: ${REASON[g.reason]}`;
}

export function InsuranceScreen() {
  const certificates = useQuery(certificatesQuery);
  const upcoming = useQuery(upcomingInsuranceGapsQuery);
  const me = useQuery(meQuery);
  const zone = me.data?.tenant.defaultTz;
  const [adding, setAdding] = useState(false);
  const expiring = certificates.data?.filter((c) => c.expiringSoon && !c.expired) ?? [];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        kicker="Compliance"
        title="Insurance"
        meta={
          certificates.data
            ? `${certificates.data.length} ${certificates.data.length === 1 ? 'certificate' : 'certificates'}`
            : undefined
        }
        actions={!adding ? <AddButton noun="Certificate" onClick={() => setAdding(true)} /> : null}
      />
      <p className="max-w-prose text-sm text-ink-soft">
        The company’s certificates of currency, matched against what each venue asks to see. Set
        what a venue asks for under Venues. Nothing is refused over insurance — a job whose venue’s
        cover isn’t met is listed below so it can be fixed with the broker.
      </p>
      {expiring.length > 0 ? (
        <p role="status" className="alert">
          Expiring within 30 days:{' '}
          {expiring
            .map((c) => `${c.kind.name} (${c.insurer}, ${dayWithYear(c.expiresOn)})`)
            .join('; ')}
        </p>
      ) : null}
      {adding ? <CertificateForm onDone={() => setAdding(false)} /> : null}

      <section aria-labelledby="certificates" className="flex flex-col gap-3">
        <h2 id="certificates" className="section-title">
          Certificates of currency
        </h2>
        {certificates.isError ? (
          <p role="alert" className="alert">
            {certificates.error.message}
          </p>
        ) : null}
        {certificates.data?.length === 0 ? (
          <p className="text-sm text-ink-soft">None recorded yet.</p>
        ) : null}
        {certificates.data && certificates.data.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="data-table">
              <caption className="sr-only">Certificates of currency, by kind</caption>
              <thead>
                <tr>
                  <th scope="col">Cover</th>
                  <th scope="col">Insurer</th>
                  <th scope="col">Amount</th>
                  <th scope="col">Period</th>
                  <th scope="col" className="sr-only">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {certificates.data.map((c) => (
                  <CertificateRow key={c.id} certificate={c} />
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      <section aria-labelledby="uncovered" className="flex flex-col gap-3">
        <h2 id="uncovered" className="section-title">
          Upcoming jobs whose venue’s cover isn’t met
        </h2>
        {upcoming.isError ? (
          <p role="alert" className="alert">
            {upcoming.error.message}
          </p>
        ) : null}
        {upcoming.data?.length === 0 ? (
          <p className="text-sm text-ink-soft">None — every upcoming job’s venue is covered.</p>
        ) : null}
        <ul className="flex flex-col gap-2">
          {upcoming.data?.map((u) => (
            <li key={u.job.id} className="flex flex-col gap-1 rounded-md bg-tile p-3 text-sm">
              <span>
                <Link to="/jobs/$jobId" params={{ jobId: u.job.id }} className="link font-semibold">
                  J-{u.job.number} {u.job.name}
                </Link>{' '}
                <span className="text-ink-soft">
                  at {u.venue.name}
                  {zone ? `, from ${dayWithYear(tenantDay(u.startsAt, zone))}` : null}
                </span>
              </span>
              {u.gaps.map((g) => (
                <span key={g.kind.id} className="tag tag-accent-2 self-start">
                  {gapLabel(g)}
                </span>
              ))}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function CertificateRow({ certificate: c }: { certificate: Certificate }) {
  const queryClient = useQueryClient();
  const retire = useMutation({
    mutationFn: () => retireCertificate(c.id),
    onSuccess: () => refreshInsurance(queryClient),
  });
  return (
    <tr>
      <th scope="row" className="font-semibold">
        {c.kind.name}
        <span className="block text-xs font-normal text-ink-soft">{c.tradingEntity.legalName}</span>
      </th>
      <td>
        {c.insurer}
        {c.policyNumber ? (
          <span className="block text-xs text-ink-soft">{c.policyNumber}</span>
        ) : null}
      </td>
      <td className="tabular-nums">{formatMinorUnits(c.cover.amountMinor, c.cover.currency)}</td>
      <td>
        {dayWithYear(c.startsOn)} – {dayWithYear(c.expiresOn)}
        {c.expired ? (
          <span className="tag tag-accent-2 ml-2">expired</span>
        ) : c.expiringSoon ? (
          <span className="tag tag-accent-2 ml-2">expiring soon</span>
        ) : null}
      </td>
      <td className="whitespace-nowrap text-right">
        {c.fileId ? (
          <button type="button" className="link mr-3" onClick={() => void openFile(c.fileId!)}>
            PDF
          </button>
        ) : null}
        <RemoveButton what={`${c.kind.name} certificate`} onClick={() => retire.mutate()} />
      </td>
    </tr>
  );
}

function refreshInsurance(queryClient: ReturnType<typeof useQueryClient>) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: certificatesQuery.queryKey }),
    queryClient.invalidateQueries({ queryKey: ['job-insurance-gaps'] }),
    queryClient.invalidateQueries({ queryKey: ['required-covers'] }),
  ]);
}

function CertificateForm({ onDone }: { onDone: () => void }) {
  const queryClient = useQueryClient();
  const kinds = useQuery(insuranceKindsQuery);
  const settings = useQuery(documentSettingsQuery);
  const entities = settings.data?.map((s) => s.entity) ?? [];
  const [kindId, setKindId] = useState('');
  const [newKind, setNewKind] = useState<string | null>(null);
  const [entityId, setEntityId] = useState('');
  const [insurer, setInsurer] = useState('');
  const [policyNumber, setPolicyNumber] = useState('');
  const [startsOn, setStartsOn] = useState('');
  const [expiresOn, setExpiresOn] = useState('');
  const [amount, setAmount] = useState('');
  const [pdf, setPdf] = useState<File | null>(null);
  const [problem, setProblem] = useState<string | undefined>();
  const insured = entityId || entities[0]?.id || '';
  const save = useMutation({
    mutationFn: async (amountMinor: string) => {
      const insuranceKindId =
        newKind !== null ? (await addInsuranceKind(newKind.trim())).id : kindId;
      const fileId = pdf ? await uploadCertificatePdf(insured, pdf) : null;
      await addCertificate({
        insuranceKindId,
        tradingEntityId: insured,
        insurer: insurer.trim(),
        policyNumber: policyNumber.trim() || null,
        startsOn,
        expiresOn,
        cover: { amountMinor, currency: 'AUD' },
        fileId,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: insuranceKindsQuery.queryKey });
      await refreshInsurance(queryClient);
      onDone();
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    if (newKind !== null ? !newKind.trim() : !kindId)
      return setProblem('Choose the kind of cover.');
    if (!insurer.trim()) return setProblem('Name the insurer.');
    if (!startsOn || !expiresOn) return setProblem('Give the period of insurance.');
    const minor = dollarsToMinorUnits(amount);
    if (!minor || minor === '0') return setProblem('Give the amount covered, in dollars.');
    setProblem(undefined);
    save.mutate(minor);
  }

  return (
    <form onSubmit={submit} className="panel flex flex-col gap-3" aria-label="Certificate">
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="certificate-kind" className="text-xs text-ink-soft">
            Kind of cover
          </label>
          <select
            id="certificate-kind"
            className="input"
            value={newKind !== null ? '+' : kindId}
            onChange={(e) => {
              const v = e.currentTarget.value;
              if (v === '+') return setNewKind('');
              setNewKind(null);
              setKindId(v);
            }}
          >
            <option value="">Choose…</option>
            {(kinds.data ?? []).map((k) => (
              <option key={k.id} value={k.id}>
                {k.name}
              </option>
            ))}
            <option value="+">+ New cover type</option>
          </select>
          {newKind !== null ? (
            <Field
              label="New kind’s name"
              hint="Public liability, Workers compensation…"
              value={newKind}
              onChange={(e) => setNewKind(e.currentTarget.value)}
            />
          ) : null}
        </div>
        {entities.length > 1 ? (
          <div className="flex flex-col gap-1.5">
            <label htmlFor="certificate-entity" className="text-xs text-ink-soft">
              Insured
            </label>
            <select
              id="certificate-entity"
              className="input"
              value={insured}
              onChange={(e) => setEntityId(e.currentTarget.value)}
            >
              {entities.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.legalName}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        <Field
          label="Insurer"
          value={insurer}
          onChange={(e) => setInsurer(e.currentTarget.value)}
        />
        <Field
          label="Policy number"
          value={policyNumber}
          onChange={(e) => setPolicyNumber(e.currentTarget.value)}
        />
        <Field
          label="Cover starts"
          type="date"
          value={startsOn}
          onChange={(e) => setStartsOn(e.currentTarget.value)}
        />
        <Field
          label="Cover ends (last day)"
          type="date"
          value={expiresOn}
          onChange={(e) => setExpiresOn(e.currentTarget.value)}
        />
        <Field
          label="Amount covered ($)"
          inputMode="decimal"
          hint="Each claim — e.g. 20000000"
          value={amount}
          onChange={(e) => setAmount(e.currentTarget.value)}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <label htmlFor="certificate-pdf" className="text-xs text-ink-soft">
          The certificate (PDF, optional)
        </label>
        <input
          id="certificate-pdf"
          type="file"
          accept="application/pdf"
          className="text-sm"
          onChange={(e) => setPdf(e.currentTarget.files?.[0] ?? null)}
        />
      </div>
      {problem || save.isError ? (
        <p role="alert" className="alert">
          {problem ?? save.error?.message}
        </p>
      ) : null}
      <div className="flex gap-2">
        <Button type="submit" disabled={save.isPending || !insured}>
          Save
        </Button>
        <Button type="button" variant="quiet" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

/**
 * VEN-05 at v1.966: the insurance a venue asks to see, each with whether today's certificates meet
 * it. Saving replaces the set; a kind left unticked is no longer asked for.
 */
export function RequiredCovers({ venueId, editable }: { venueId: string; editable: boolean }) {
  const queryClient = useQueryClient();
  const me = useQuery(meQuery);
  const kinds = useQuery(insuranceKindsQuery);
  const covers = useQuery(requiredCoversQuery(venueId));
  const mayAddKind = me.data?.capabilities.includes('documents.manage') ?? false;
  const [editing, setEditing] = useState(false);
  // kind id → the minimum in dollars as typed ('' for none); absent means not asked.
  const [chosen, setChosen] = useState<Record<string, string>>({});
  const [newKind, setNewKind] = useState('');
  const [problem, setProblem] = useState<string | undefined>();
  const save = useMutation({
    mutationFn: async (list: Array<{ insuranceKindId: string; minor: string | null }>) => {
      const extra = newKind.trim()
        ? [{ insuranceKindId: (await addInsuranceKind(newKind.trim())).id, minor: null }]
        : [];
      return setRequiredCovers(
        venueId,
        [...list, ...extra].map((c) => ({
          insuranceKindId: c.insuranceKindId,
          minimum: c.minor ? { amountMinor: c.minor, currency: 'AUD' } : null,
        })),
      );
    },
    onSuccess: async () => {
      setEditing(false);
      setNewKind('');
      await queryClient.invalidateQueries({ queryKey: ['required-covers', venueId] });
      await queryClient.invalidateQueries({ queryKey: insuranceKindsQuery.queryKey });
      await queryClient.invalidateQueries({ queryKey: ['job-insurance-gaps'] });
    },
  });

  function submit() {
    const list: Array<{ insuranceKindId: string; minor: string | null }> = [];
    for (const [insuranceKindId, dollars] of Object.entries(chosen)) {
      if (!dollars.trim()) {
        list.push({ insuranceKindId, minor: null });
        continue;
      }
      const minor = dollarsToMinorUnits(dollars);
      if (!minor || minor === '0')
        return setProblem('A minimum is an amount in dollars, or empty.');
      list.push({ insuranceKindId, minor });
    }
    setProblem(undefined);
    save.mutate(list);
  }

  if (!editing) {
    return (
      <p className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-ink-soft">Insurance asked for:</span>
        {covers.data && covers.data.length === 0 ? <span>none</span> : null}
        {covers.data?.map((c) => (
          <CoverTag key={c.kind.id} cover={c} />
        ))}
        {editable ? (
          <button
            type="button"
            className="link"
            onClick={() => {
              setChosen(
                Object.fromEntries(
                  (covers.data ?? []).map((c) => [
                    c.kind.id,
                    c.minimum
                      ? minorUnitsToDollars(c.minimum.amountMinor).replace(/\.00$/, '')
                      : '',
                  ]),
                ),
              );
              setEditing(true);
            }}
          >
            Edit<span className="sr-only"> the insurance asked for</span>
          </button>
        ) : null}
      </p>
    );
  }
  return (
    <fieldset className="flex flex-col gap-2 rounded-md bg-tile p-3">
      <legend className="text-sm font-semibold">Insurance this venue asks to see</legend>
      {kinds.data?.length === 0 && !mayAddKind ? (
        <p className="text-sm text-ink-soft">
          No kinds of cover yet. Someone who manages documents adds them under Insurance.
        </p>
      ) : null}
      <ul className="flex flex-col gap-2 text-sm">
        {(kinds.data ?? []).map((k) => {
          const on = k.id in chosen;
          return (
            <li key={k.id} className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={on}
                  onChange={(e) => {
                    const checked = e.currentTarget.checked;
                    setChosen((c) => {
                      const next = { ...c };
                      if (checked) next[k.id] = '';
                      else delete next[k.id];
                      return next;
                    });
                  }}
                />
                {k.name}
              </label>
              {on ? (
                <label className="flex items-center gap-2 text-ink-soft">
                  at least $
                  <input
                    className="input w-36"
                    inputMode="decimal"
                    aria-label={`${k.name} minimum in dollars`}
                    placeholder="no minimum"
                    value={chosen[k.id]}
                    onChange={(e) => {
                      const v = e.currentTarget.value;
                      setChosen((c) => ({ ...c, [k.id]: v }));
                    }}
                  />
                </label>
              ) : null}
            </li>
          );
        })}
      </ul>
      {mayAddKind ? (
        <Field
          label="Add another kind of cover (optional)"
          value={newKind}
          onChange={(e) => setNewKind(e.currentTarget.value)}
        />
      ) : null}
      {problem || save.isError ? (
        <p role="alert" className="alert">
          {problem ?? save.error?.message}
        </p>
      ) : null}
      <div className="flex gap-2">
        <Button type="button" disabled={save.isPending} onClick={submit}>
          Save
        </Button>
        <Button type="button" variant="quiet" onClick={() => setEditing(false)}>
          Cancel
        </Button>
      </div>
    </fieldset>
  );
}

function CoverTag({ cover: c }: { cover: RequiredCover }) {
  const minimum = c.minimum
    ? ` ≥ ${formatMinorUnits(c.minimum.amountMinor, c.minimum.currency).replace(/\.00$/, '')}`
    : '';
  return (
    <span className={`tag ${c.gapToday ? 'tag-accent-2' : ''}`}>
      {c.kind.name}
      {minimum}
      {c.gapToday ? ` — ${c.gapToday === 'lapses' ? 'not covered today' : REASON[c.gapToday]}` : ''}
    </span>
  );
}

/** CMP-06 at v1.966: on a job, what its venue asks that the certificates don't meet. */
export function JobInsuranceGaps({ jobId }: { jobId: string }) {
  const gaps = useQuery(jobInsuranceGapsQuery(jobId));
  if (!gaps.data || gaps.data.length === 0) return null;
  return (
    <p role="status" className="alert flex flex-col gap-1">
      <span className="font-semibold">The venue’s insurance isn’t met</span>
      {gaps.data.map((g) => (
        <span key={g.kind.id}>{gapLabel(g)}</span>
      ))}
    </p>
  );
}
